import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { jt } from "ttag";

import type { ReviewsBlock as ReviewsBlockValue } from "@reactivated";

import styles from "./index.module.scss";

const THEME_STARS_BG = "theme--stars-and-bg";

type IReview = NonNullable<NonNullable<ReviewsBlockValue["reviews"]>[number]>;

const isStarsTheme = (theme?: string | null): boolean => {
    return theme === THEME_STARS_BG;
};

const renderAuthor = (
    author: string,
    location: string,
    theme?: string | null,
): React.ReactNode => {
    const authorEm = <em key="reviews-block-author-em">{author}</em>;
    const authorStrong = (
        <strong key="reviews-block-author-strong">{author}</strong>
    );
    const locationSpan = <span key="reviews-block-location">{location}</span>;

    if (isStarsTheme(theme)) {
        return <>{jt`– ${authorEm} in ${locationSpan}`}</>;
    }
    return (
        <>
            {authorStrong}, {locationSpan}
        </>
    );
};

/**
 * The interactive inner-review carousel of a ``ReviewsBlock`` (#33456).
 *
 * Converted off the SSR-V1 ``block_data`` JSON-string contract to native
 * Reactivated serialization: it now reads the typed ``reviews`` list +
 * ``theme`` off the block ``value`` (no ``JSON.parse`` / io-ts decode). The
 * outer ``.reviews-block`` wrapper, pre-header, title, star-rating and CTA
 * remain in the Django shim (``cms/blocks/reviews.html``); this island owns
 * only the Flickity-carousel review columns -- identical to the SSR-V1 split.
 */
export const ReviewsBlock = (
    props: Immutable<{
        reviews: readonly IReview[];
        theme?: string | null;
    }>,
): React.ReactNode => {
    const carouselRef = React.useRef<HTMLDivElement | null>(null);

    useFlickity(carouselRef, {
        options: {
            watchCSS: true,
            wrapAround: true,
            prevNextButtons: false,
            pageDots: true,
        },
        enabled: !isStarsTheme(props.theme),
    });

    const columnClasses = classNames({
        [styles.column]: true,
        [styles.columnThemeStars]: isStarsTheme(props.theme),
    });
    const contentClasses = classNames({
        [styles.content]: true,
        [styles.contentThemeStars]: isStarsTheme(props.theme),
    });
    const authorClasses = classNames({
        [styles.author]: true,
        [styles.authorThemeStars]: isStarsTheme(props.theme),
    });

    return (
        <div className={styles.columns} ref={carouselRef}>
            {props.reviews.map((review, i) => (
                <div className={columnClasses} key={i}>
                    <div className={contentClasses}>{review.content}</div>
                    <div className={authorClasses}>
                        {renderAuthor(
                            review.author ?? "",
                            review.location ?? "",
                            props.theme,
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
